import { Separator } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLabel, IPSpinner, IPSpinnerSize, IPStack, IPText } from 'baseComponents';
import GenericModal from 'components/Dialogs/GenericModal';
import HrefLink from 'components/Links/HrefLink';
import { getUserRoleGroupItem, renderUserRoleGroupItem } from 'components/Pickers/UserRoleGroupPicker';
import { globalStackTokensGapMedium } from 'globalStyles';
import { PermissionTypes } from 'models/auth/rolePermission';
import ProcessingActivity from 'models/processingActivity';
import Task from 'models/tasks/task';
import { SystemTaskTypes } from 'models/tasks/taskType';
import User from 'models/user';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetUsersWithPermission } from 'services/Api/authService';
import { apiCreateProcessingActivityApproval } from 'services/Api/processingActivityService';
import { apiRequest } from 'services/Auth/authConfig';
import { sortOnString } from 'utils/sorting';
import { createNewProcessingActivityApproval } from '../Approval/ApprovalHelpers';

interface ICreateProcessingActivityApprovalModalProps {
  selectedProcessingActivities: ProcessingActivity[];
  updateProcessingActivities: (processingActivities: ProcessingActivity[]) => void;
  isOpen: boolean;
  onClose: () => void;
}

export const CreateProcessingActivityApprovalModal = (props: ICreateProcessingActivityApprovalModalProps) => {
  const { t } = useTranslation(['library', 'translation', 'task']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [template, setTemplate] = useState<Task | undefined>(undefined);
  const [viewers, setViewers] = useState<User[]>([]);
  const [approvers, setApprovers] = useState<User[]>([]);

  useEffect(() => {
    if (template === undefined || viewers.length === 0) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      //try to find the processing activity approval template
      const _templates = await appContext.globalDataCache.templates.getItems();
      const approvalTemplate = _templates.find((t) => t.systemTaskType === SystemTaskTypes.ProcessingActivityApproval);
      //get all users with approve processing activity permission
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const _allApprovers = await apiGetUsersWithPermission(
        PermissionTypes.ApproveProcessingActivity,
        accessToken,
        appContext.globalDataCache,
      );
      //get viewers from the template instances
      let _viewers: User[] = [];
      if (approvalTemplate) {
        approvalTemplate.instances ??= [];
        _viewers = approvalTemplate.instances.map((i) => i.user).filter((u) => u !== undefined) as User[];
        _viewers.sort((a, b) => sortOnString(a.name, b.name));
      }
      //set state
      setViewers(_viewers);
      setApprovers(_viewers.filter((u) => _allApprovers.some((a) => a.id === u.id)));
      setTemplate(approvalTemplate);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const createApprovals = async () => {
    if (!template || approvers.length === 0) return;
    try {
      setIsLoading(true);
      const newProcessingActivities: ProcessingActivity[] = [];
      for (const processingActivity of props.selectedProcessingActivities) {
        const task = createNewProcessingActivityApproval(processingActivity, template, appContext, t);

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const newProcesingActivity = await apiCreateProcessingActivityApproval(
          task,
          accessToken,
          appContext.globalDataCache,
        );

        newProcessingActivities.push(newProcesingActivity);
      }
      props.updateProcessingActivities(newProcessingActivities);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
      props.onClose();
    }
  };

  return (
    <GenericModal
      title={t('processingActivities:Panels.CreateApproval.Title')}
      subTitle={t('processingActivities:Panels.CreateApproval.SubTitle')}
      saveButtonText={t('translation:General.Button.Create')}
      isOpen={props.isOpen}
      onClose={props.onClose}
      onSave={createApprovals}
      saveDisabled={isLoading || !template || approvers.length === 0}
      width={'40vw'}
      minWidth={250}
      maxWidth={'95vw'}
      hideFooter={false}
    >
      <Separator />
      <IPStack tokens={globalStackTokensGapMedium}>
        {isLoading && <IPSpinner size={IPSpinnerSize.large} />}
        {!isLoading && approvers.length > 0 && (
          <IPStack>
            <IPLabel>{t('processingActivities:Panels.CreateApproval.ViewersLabel')}</IPLabel>
            <IPStack horizontal wrap>
              {viewers.map((item) => {
                return renderUserRoleGroupItem(
                  getUserRoleGroupItem(item),
                  true,
                  undefined,
                  false,
                  undefined,
                  undefined,
                  true,
                );
              })}
            </IPStack>
          </IPStack>
        )}
        {!isLoading && approvers.length === 0 && (
          <IPLabel>{t('processingActivities:Panels.CreateApproval.NoViewersLabel')}</IPLabel>
        )}
        {!isLoading && approvers.length === 0 && appContext.user.isAdmin() && (
          <HrefLink href="/admin/settings/modules#processingactivities">
            {t('processingActivities:Panels.CreateApproval.SettingsLink')}
          </HrefLink>
        )}
        {!isLoading && approvers.length === 0 && !appContext.user.isAdmin() && (
          <IPText>{t('processingActivities:Panels.CreateApproval.ContactAdminLabel')}</IPText>
        )}
      </IPStack>
    </GenericModal>
  );
};
